/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useContext, ReactNode } from 'react';

import { AnyFunction, AnyObject } from 'types';

import { capitalize } from 'utils/text';

/*
  ---------> WARNING <---------

  Due to how vite HMR works, using this `quickContext` helper may invalidate the entire
  tree and cause either a full page reload or a reload to a lot more components may be caused

  Generally speaking, you'd want to create contexts splitting files, like we do
  for almost all `src/Providers` in this app. That means the createContext+useX definitions
  happen on the xx.context.ts file and the Provider definition happens on another file

  After that, you also split the imports across the app, meaning consumers (useX) will import it
  from a place that DOES NOT also export the Provider. If that happens, the HMR won't work as expected
  causing state-loss

  This is a quick wrapper that encapsulates the steps of creating a context + automatically typing
  it based on type infers, but it has its trade-offs. Use wisely.
*/

interface BaseProviderProps {
  children: ReactNode;
}

type FirstParams<Fn extends AnyFunction> = Parameters<Fn>[0];

type HasParams<Fn extends AnyFunction, Y, N> = undefined extends FirstParams<Fn> ? N : Y;

type UseBuilderBase = (...params: any[]) => { data: AnyObject; content?: ReactNode };

type ProviderProps<UseBuilder extends UseBuilderBase> = HasParams<
  UseBuilder,
  BaseProviderProps & FirstParams<UseBuilder>,
  BaseProviderProps
>;

type CtxData<UseBuilder extends UseBuilderBase> = ReturnType<UseBuilder>['data'];

type AnyComponent<Props = any> = (
  ...params: undefined extends Props ? [] : [Props]
) => JSX.Element | null;

type QuickContext<Name extends string, UseBuilder extends UseBuilderBase> = {
  [key in `use${Capitalize<Name>}`]: () => CtxData<UseBuilder>;
} & {
  [Provider in `${Capitalize<Name>}Provider`]: (props: ProviderProps<UseBuilder>) => JSX.Element;
} & {
  [Provider in `with${Capitalize<Name>}Context`]: <
    CompProps extends HasParams<UseBuilder, FirstParams<UseBuilder>, any>,
  >(
    comp: AnyComponent<CompProps>,
  ) => AnyComponent<CompProps>;
} & {
  [Provider in `create${Capitalize<Name>}Provider`]: () => (
    props: ProviderProps<UseBuilder>,
  ) => JSX.Element;
};

const contexts: AnyObject = {};

export function quickContext<Name extends string, UseBuilder extends UseBuilderBase>(
  name: Name,
  useBuilder: UseBuilder,
): QuickContext<Name, UseBuilder> {
  contexts[name] ??= createContext(null as any);

  const Context = contexts[name];

  const capitalized = capitalize(name);

  const hookName = `use${capitalized}`;
  const providerName = `${capitalized}Provider`;
  const withContextName = `with${capitalized}Context`;
  const creatorName = `create${capitalized}Provider`;

  function ContextWrapper({ children, ...props }: ProviderProps<UseBuilder>) {
    const { data, content } = useBuilder(props);

    return (
      <Context.Provider value={data}>
        {content}

        <>{children}</>
      </Context.Provider>
    );
  }

  return {
    [hookName]() {
      const ctx = useContext(Context);

      if (!ctx) throw new Error(`${hookName} was used outside its provider`);

      return ctx;
    },

    [providerName]: ContextWrapper,

    [withContextName](Component: AnyComponent) {
      return (props: any = {}) => (
        <ContextWrapper {...props}>
          <Component {...props} />
        </ContextWrapper>
      );
    },

    [creatorName]: () => ContextWrapper,
  } as QuickContext<Name, UseBuilder>;
}
